
import { useState, useEffect } from "react";
import { ArrowUpDown } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tip } from "@/types";
import { supabase } from "@/integrations/supabase/client";
import { TipOperations } from "./tips-table/TipOperations";

interface TipsTableProps {
  tips: Tip[];
  onEdit: (tip: Tip) => void;
  onArchive: (id: number) => void;
  onUnarchive: (id: number) => void;
  onApprove: (id: number) => void;
  onClearVotes: (id: number) => void;
  showArchived: boolean;
}

type SortField = 'title' | 'unsw_votes' | 'usyd_votes' | 'uoa_votes' | 'created_at' | 'status';
type SortDirection = 'asc' | 'desc';

interface University {
  id: number;
  name: string;
}

export function TipsTable({ 
  tips: initialTips, 
  onEdit, 
  onArchive, 
  onUnarchive, 
  onApprove,
  onClearVotes,
  showArchived 
}: TipsTableProps) {
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [universities, setUniversities] = useState<Record<number, string>>({});

  useEffect(() => {
    const fetchUniversities = async () => {
      const { data, error } = await supabase
        .from('universities')
        .select('id, name');
      
      if (!error && data) {
        const universityMap = data.reduce((acc, univ) => ({
          ...acc,
          [univ.id]: univ.name
        }), {});
        setUniversities(universityMap);
      }
    };

    fetchUniversities();
  }, []);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedTips = [...initialTips].sort((a, b) => {
    const modifier = sortDirection === 'asc' ? 1 : -1;
    
    switch (sortField) {
      case 'title':
        return modifier * a.title.localeCompare(b.title);
      case 'unsw_votes':
        return modifier * ((a.unsw_votes || 0) - (b.unsw_votes || 0));
      case 'usyd_votes':
        return modifier * ((a.usyd_votes || 0) - (b.usyd_votes || 0));
      case 'uoa_votes':
        return modifier * ((a.uoa_votes || 0) - (b.uoa_votes || 0));
      case 'status':
        return modifier * a.status.localeCompare(b.status);
      default:
        return modifier * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    }
  });

  const getUniversityDisplay = (tip: Tip) => {
    if (tip.scope === 'all_universities') {
      return "All Universities";
    }
    return tip.university_id ? universities[tip.university_id] : "Unspecified";
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead onClick={() => handleSort('title')} className="cursor-pointer hover:bg-muted/50">
            Title <ArrowUpDown className="inline h-4 w-4 ml-1" />
          </TableHead>
          <TableHead className="max-w-[300px]">Content</TableHead>
          <TableHead>Emoji</TableHead>
          <TableHead onClick={() => handleSort('unsw_votes')} className="cursor-pointer hover:bg-muted/50">
            UNSW Votes <ArrowUpDown className="inline h-4 w-4 ml-1" />
          </TableHead>
          <TableHead onClick={() => handleSort('usyd_votes')} className="cursor-pointer hover:bg-muted/50">
            USYD Votes <ArrowUpDown className="inline h-4 w-4 ml-1" />
          </TableHead>
          <TableHead onClick={() => handleSort('uoa_votes')} className="cursor-pointer hover:bg-muted/50">
            UOA Votes <ArrowUpDown className="inline h-4 w-4 ml-1" />
          </TableHead>
          <TableHead onClick={() => handleSort('status')} className="cursor-pointer hover:bg-muted/50">
            Status <ArrowUpDown className="inline h-4 w-4 ml-1" />
          </TableHead>
          <TableHead>University</TableHead>
          <TableHead onClick={() => handleSort('created_at')} className="cursor-pointer hover:bg-muted/50">
            Created At <ArrowUpDown className="inline h-4 w-4 ml-1" />
          </TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedTips.map((tip) => (
          <TableRow key={tip.id} className={tip.archived ? "opacity-60" : ""}>
            <TableCell className="font-medium">{tip.title}</TableCell>
            <TableCell className="max-w-[300px] truncate">{tip.content || "No content"}</TableCell>
            <TableCell>{tip.emoji}</TableCell>
            <TableCell>{tip.unsw_votes || 0}</TableCell>
            <TableCell>{tip.usyd_votes || 0}</TableCell>
            <TableCell>{tip.uoa_votes || 0}</TableCell>
            <TableCell>{tip.status}</TableCell>
            <TableCell>{getUniversityDisplay(tip)}</TableCell>
            <TableCell>{new Date(tip.created_at).toLocaleDateString()}</TableCell>
            <TableCell>
              <TipOperations
                tip={tip}
                onEdit={onEdit}
                onArchive={onArchive}
                onUnarchive={onUnarchive}
                onApprove={onApprove}
                onClearVotes={onClearVotes}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
